import { icons } from '@shared/static/icons';
import { FormGroup } from '@shared/ui/forms/display';
import { Controller, useWatch } from 'react-hook-form';
import { InputGroup, SelectPicker } from 'rsuite';
import Note from '@shared/ui/messages/Note';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@mui/material';
import { RenderItemBank } from '@shared/ui/forms/select-render';
import { useMemo } from 'react';
import { formatBalanceUi } from '@utils/balance-helpers';
import { useTransferDialog, useTopUpBalanceDialog } from '@utils/dialogs-hooks';
import { useSelector } from 'react-redux';
import { roles } from '@shared/static/roles';

export default function BankSelect({
  control,
  name,
  label,
  notEnough = 0,
  data = [],
  displayBalance = false,
  error,
  valueKey = 'bank_id',
  isRequired = false,
  isTransfer = false,
  disabled = false,
}) {
  const { t } = useTranslation();
  const bankWatch = useWatch({ control, name });
  const { openTransferDialog } = useTransferDialog();
  const { openTopUpDialog } = useTopUpBalanceDialog();
  const { user } = useSelector((state) => state.user);
  const isMember = user?.group_role === roles.TEAM_MEMBER;

  const selectedWallet = useMemo(
    () => data.find((x) => x[valueKey] === bankWatch),
    [data, bankWatch, valueKey],
  );

  const options = useMemo(
    () =>
      data.filter((x) => x?.[valueKey] !== null && x?.[valueKey] !== undefined),
    [data, valueKey],
  );

  const renderItem = (_, item) => (
    <RenderItemBank item={item} displayBalance={displayBalance} />
  );

  return (
    <FormGroup label={label} error={error}>
      {!data.length && bankWatch ? (
        <Skeleton
          height={58}
          style={{ transform: 'none' }}
          sx={{ bgcolor: 'grey.100' }}
        />
      ) : (
        <>
          <Controller
            control={control}
            name={name}
            rules={{ required: isRequired }}
            render={({ field }) => (
              <InputGroup
                inside
                className={
                  field.value !== null && field.value !== undefined
                    ? 'has-value'
                    : ''
                }
              >
                <SelectPicker
                  className="select-bank"
                  data={options}
                  block
                  value={field.value}
                  onChange={field.onChange}
                  valueKey={valueKey}
                  labelKey="bank_public_name"
                  searchable={false}
                  disabled={disabled}
                  caret={!disabled}
                  placeholder={t('select_bank_placeholder')}
                  renderOption={renderItem}
                  renderValue={renderItem}
                />
              </InputGroup>
            )}
          />
          {notEnough > 0 && (
            <div className="select-bank-note">
              <Note
                singleLine
                type="warning"
                content={
                  isMember
                    ? t('insufficient_balance_member')
                    : t('not_enough_bank_balance', {
                        amount: formatBalanceUi(
                          notEnough,
                          selectedWallet?.currency,
                        ),
                      })
                }
              >
                {!isMember && (
                  <button
                    type="button"
                    className="copy-btn"
                    onClick={() =>
                      isTransfer
                        ? openTransferDialog({
                            bank_id: selectedWallet?.bank_id,
                            bank_from: 0,
                            amount: notEnough,
                          })
                        : openTopUpDialog()
                    }
                  >
                    {icons['arrow-up']}
                    {t('Top Up')}
                  </button>
                )}
              </Note>
            </div>
          )}
        </>
      )}
    </FormGroup>
  );
}
